import { SK_OFFLINE_QUEUE } from './constants/storageKeys';

export interface QueuedRequest {
  id: string;
  method: 'POST' | 'PUT';
  path: string;
  body: object | null;
  token: string | null;
  createdAt: string;
  retries: number;
}

const OFFLINEABLE_PATHS = [
  '/api/daily-checkins',
  '/api/events',
  '/api/medication-logs',
  '/api/crisis-plan',
  '/api/ed/visits',
  '/api/care-team',
  '/api/support-network',
];

const ED_CHECKIN_RE = /^\/api\/ed\/visits\/\d+\/checkins$/;
const CARE_TEAM_ID_RE = /^\/api\/care-team\/\d+$/;
const SUPPORT_NETWORK_ID_RE = /^\/api\/support-network\/\d+$/;

export function isOfflineable(path: string): boolean {
  return (
    OFFLINEABLE_PATHS.includes(path) ||
    ED_CHECKIN_RE.test(path) ||
    CARE_TEAM_ID_RE.test(path) ||
    SUPPORT_NETWORK_ID_RE.test(path)
  );
}

/** Maximum age for queued items — discard anything older during replay. */
const MAX_QUEUE_AGE_MS = 72 * 60 * 60 * 1000; // 72 hours

function notifyChange() {
  window.dispatchEvent(new CustomEvent('cw-queue-change'));
}

function readQueue(): QueuedRequest[] {
  try {
    const raw = localStorage.getItem(SK_OFFLINE_QUEUE);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedRequest[]) {
  localStorage.setItem(SK_OFFLINE_QUEUE, JSON.stringify(queue));
}

export function enqueue(item: Omit<QueuedRequest, 'id' | 'createdAt' | 'retries'>): void {
  const queue = readQueue();
  queue.push({
    ...item,
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
    createdAt: new Date().toISOString(),
    retries: 0,
  });
  writeQueue(queue);
  notifyChange();
}

export function dequeue(id: string): void {
  const queue = readQueue().filter(q => q.id !== id);
  writeQueue(queue);
  notifyChange();
}

export function getQueue(): QueuedRequest[] {
  return readQueue();
}

export function getQueueLength(): number {
  return readQueue().length;
}

export function clearQueue(): void {
  localStorage.removeItem(SK_OFFLINE_QUEUE);
  notifyChange();
}

/** Retry-aware fetch that handles Neon cold-start 503s during queue replay. */
async function replayFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status === 503) {
    const retryAfter = parseInt(res.headers.get('Retry-After') || '3', 10);
    await new Promise(r => setTimeout(r, retryAfter * 1000));
    return fetch(input, init);
  }
  return res;
}

let isReplaying = false;

export async function replayQueue(): Promise<{ succeeded: number; failed: number }> {
  if (isReplaying) return { succeeded: 0, failed: 0 };
  isReplaying = true;

  const queue = readQueue();
  let succeeded = 0;
  let consecutiveFailures = 0;
  const now = Date.now();

  for (const item of queue) {
    if (consecutiveFailures >= 3) break;

    // P-7: Discard items older than MAX_QUEUE_AGE_MS
    const age = now - new Date(item.createdAt).getTime();
    if (age > MAX_QUEUE_AGE_MS) {
      dequeue(item.id);
      succeeded++; // count as resolved so the banner updates
      continue;
    }

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (item.token) headers['Authorization'] = `Bearer ${item.token}`;

      const res = await replayFetch(item.path, {
        method: item.method,
        headers,
        credentials: 'include',
        body: item.body ? JSON.stringify(item.body) : undefined,
      });

      if (res.ok) {
        dequeue(item.id);
        succeeded++;
        consecutiveFailures = 0;
      } else if (res.status === 401) {
        // Token expired — keep in queue, stop replaying
        break;
      } else {
        item.retries++;
        consecutiveFailures++;
      }
    } catch {
      item.retries++;
      consecutiveFailures++;
    }
  }

  // Persist updated retry counts for items that failed
  const remaining = readQueue();
  for (const item of remaining) {
    const updated = queue.find(q => q.id === item.id);
    if (updated) item.retries = updated.retries;
  }
  writeQueue(remaining);

  isReplaying = false;
  notifyChange();
  return { succeeded, failed: remaining.length };
}
